import { useState } from 'react';
import { Sparkles, Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const OptimizeButton = ({ page, mois, className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const analyze = async () => {
    setLoading(true);
    setShowPanel(true);
    setAnalysis(null);
    try {
      const currentMois = mois || new Date().toISOString().slice(0, 7);
      const res = await api.post(`/optimize/${page}`, { mois: currentMois });
      setAnalysis(res.data.analysis);
    } catch {
      setAnalysis("Erreur lors de l'analyse. Reessayez.");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get(`/optimize/history/${page}`);
      setHistory(res.data);
      setShowHistory(!showHistory);
    } catch {
      setHistory([]);
    }
  };

  const formatAnalysis = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('##')) {
        return <h3 key={i} className="text-lg font-bold mt-4 mb-2 text-lemas-gold">{line.replace(/^#+\s*/, '')}</h3>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-bold mt-3 mb-1 text-[rgb(var(--lm-text))]">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return <p key={i} className="ml-4 text-sm text-[rgb(var(--lm-text-secondary))] leading-relaxed">{line}</p>;
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-sm text-[rgb(var(--lm-text-secondary))] leading-relaxed">{line}</p>;
    });
  };

  return (
    <>
      <Button
        onClick={analyze}
        disabled={loading}
        className={`bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-lemas-gold border border-lemas-gold/30 hover:bg-lemas-gold/20 transition-all ${className}`}
        data-testid={`optimize-btn-${page}`}
      >
        {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
        {loading ? 'Analyse en cours...' : 'IA Optimiser'}
      </Button>

      {showPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !loading && setShowPanel(false)}>
          <div
            className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl animate-slide-up mx-4"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[rgb(var(--lm-border))] bg-gradient-to-r from-amber-500/10 to-orange-500/10">
              <div className="flex items-center gap-2">
                <Sparkles size={20} className="text-lemas-gold" />
                <h2 className="font-title text-lg">Analyse IA</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={loadHistory} className="text-xs text-[rgb(var(--lm-text-secondary))]" data-testid="optimize-history-btn">
                  {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  Historique
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowPanel(false)} disabled={loading}>
                  <X size={16} />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 overflow-y-auto max-h-[60vh]">
              {loading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 size={32} className="animate-spin text-lemas-gold" />
                  <p className="text-sm text-[rgb(var(--lm-text-secondary))]">Analyse de vos donnees...</p>
                </div>
              )}
              {!loading && analysis && (
                <div className="prose prose-sm" data-testid="optimize-result">
                  {formatAnalysis(analysis)}
                </div>
              )}

              {/* History */}
              {showHistory && history.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[rgb(var(--lm-border))]">
                  <p className="text-xs text-[rgb(var(--lm-text-muted))] mb-2">Analyses precedentes</p>
                  {history.slice(0, 5).map((h, i) => (
                    <div key={i} className="mb-3 p-3 rounded-lg bg-lemas-bg/40 cursor-pointer hover:bg-lemas-bg/60 transition-colors" onClick={() => { setAnalysis(h.response); setShowHistory(false); }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[rgb(var(--lm-text-secondary))]">{h.mois}</span>
                        <span className="text-xs text-[rgb(var(--lm-text-muted))]">{new Date(h.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1 line-clamp-2">{h.response?.slice(0, 120)}...</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {!loading && analysis && (
              <div className="p-3 border-t border-[rgb(var(--lm-border))] flex justify-between">
                <Button size="sm" variant="ghost" onClick={analyze} className="text-xs text-lemas-gold">
                  <Sparkles size={12} className="mr-1" /> Re-analyser
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowPanel(false)} className="text-xs">
                  Fermer
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default OptimizeButton;
